import { Link, NavLink, useNavigate } from "react-router-dom";
import { PawPrint, Sparkles, Coins, LogOut, Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const links = [
  { to: "/studio", label: "Studio", auth: true },
  { to: "/gallery", label: "My Gallery", auth: true },
  { to: "/community", label: "Community" },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const visible = links.filter((l) => !l.auth || user);

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-[#FAF7F2]/85 border-b border-[#EAE3D9]" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-6">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
          <span className="h-9 w-9 rounded-xl bg-[#FF5A79] sticker grid place-items-center -rotate-6">
            <PawPrint className="h-5 w-5 text-white" />
          </span>
          <span className="font-display text-xl font-bold tracking-tight">Cutie Parlor</span>
        </Link>
        <nav className="hidden md:flex items-center gap-1">
          {visible.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) => `px-4 py-2 rounded-full text-sm font-semibold transition-colors ${isActive ? "bg-[#1E1B26] text-white" : "hover:bg-[#F5F0E8]"}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-3">
          {user ? (
            <>
              <Link to="/credits" data-testid="nav-credits-balance"
                className="flex items-center gap-1.5 rounded-full bg-[#FFC72C] sticker sticker-press px-3 py-1.5 text-sm font-bold">
                <Coins className="h-4 w-4" /> {user.credits}
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="nav-user-menu" className="h-9 w-9 rounded-full overflow-hidden bg-[#8A4FFF] text-white font-bold grid place-items-center border-2 border-[#1E1B26]">
                    {user.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : user.name?.[0]?.toUpperCase()}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 rounded-2xl">
                  <div className="px-2 py-1.5 text-sm font-semibold truncate">{user.name}</div>
                  {visible.map((l) => (
                    <DropdownMenuItem key={l.to} className="md:hidden" onClick={() => nav(l.to)}>{l.label}</DropdownMenuItem>
                  ))}
                  {user.role === "admin" && <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">Order review</DropdownMenuItem>}
                  <DropdownMenuItem onClick={() => nav("/credits")} data-testid="menu-credits"><Coins className="h-4 w-4 mr-2" />Credits</DropdownMenuItem>
                  <DropdownMenuItem data-testid="nav-logout-button" onClick={async () => { await logout(); nav("/"); }}>
                    <LogOut className="h-4 w-4 mr-2" />Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : user === false ? (
            <>
              <Link to="/community" className="md:hidden p-2" data-testid="nav-mobile-community"><Menu className="h-5 w-5" /></Link>
              <Link to="/login" className="hidden sm:inline text-sm font-semibold px-3" data-testid="nav-login-button">Log in</Link>
              <Link to="/signup" data-testid="nav-signup-button"
                className="flex items-center gap-1.5 rounded-full bg-[#FF5A79] text-white sticker sticker-press px-4 py-2 text-sm font-bold">
                <Sparkles className="h-4 w-4" /> Start free
              </Link>
            </>
          ) : null}
        </div>
      </div>
    </header>
  );
};
